/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/label-has-for */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import handleKeyPress from '../../../../common/handleKeyPress';
import Input from '../../../../componentLibrary/Input';
import InputValidator from '../../../../componentLibrary/InputValidator';
import * as Styled from '../styles';

const ValidatedLink = InputValidator(Input, { url: 1 });

const ActiveIcon = ({
    title,
    isAnimated,
    direction,
    activeIcons,
    updateIconsOrder,
    first,
    last,
    icon,
    icons,
    updateContent,
    href,
    iconSrc,
    id,
    hasFullStylingAccess,
}) => {
    const [iconHref, setIconHref] = useState(href);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (iconHref === href) {
                return;
            }
            const newIcons = icons.map((newIcon) => {
                if (newIcon.title !== title) {
                    return newIcon;
                }

                return { ...newIcon, href: iconHref };
            });
            updateContent('icons', newIcons);
        }, 1000);

        return () => clearTimeout(delayDebounceFn);
    }, [iconHref]);

    const onMoveUpClick = () => {
        if (first) {
            return;
        }
        const activeArrayIndex = activeIcons.findIndex((cbIcon) => cbIcon.title === title);
        const targetIcon = activeIcons[activeArrayIndex - 1];
        updateIconsOrder(icon, targetIcon, 'top');
    };

    const onMoveDownClick = () => {
        if (last) {
            return;
        }
        const activeArrayIndex = activeIcons.findIndex((cbIcon) => cbIcon.title === title);
        const targetIcon = activeIcons[activeArrayIndex + 1];
        updateIconsOrder(icon, targetIcon, 'bottom');
    };

    const onDeleteClick = () => {
        if (activeIcons.length === 1) {
            return;
        }
        const newIcons = icons.map((newIcon) => {
            if (newIcon.title !== title) {
                return newIcon;
            }

            return { ...newIcon, active: !newIcon.active };
        });
        updateContent('icons', newIcons);
    };

    return (
        <Styled.ActiveIconWrapper animate={isAnimated} direction={direction} id={id}>
            <Styled.ActiveIconHeader>
                <Styled.SmallLabel htmlFor={`${title}-input`}>{title.replace('-', ' ')}</Styled.SmallLabel>
                <div style={{ height: 14 }}>
                    <Styled.MoveIcon
                        className="material-icons"
                        onClick={() => onMoveUpClick()}
                        onKeyDown={(e) => handleKeyPress(e, onMoveUpClick)}
                        tabIndex={0}
                        role="button"
                        aria-label="Move social icon up in order"
                        disabled={first}
                    >
                        arrow_upward
                    </Styled.MoveIcon>
                    <Styled.MoveIcon
                        className="material-icons"
                        onClick={() => onMoveDownClick()}
                        onKeyDown={(e) => handleKeyPress(e, onMoveDownClick)}
                        tabIndex={0}
                        role="button"
                        aria-label="Move social icon down in order"
                        disabled={last}
                    >
                        arrow_downward
                    </Styled.MoveIcon>
                    {hasFullStylingAccess && (
                        <Styled.DeleteIcon
                            disabled={activeIcons.length === 1}
                            className="material-icons"
                            onClick={() => onDeleteClick()}
                            onKeyDown={(e) => handleKeyPress(e, onDeleteClick)}
                            tabIndex={0}
                            role="button"
                            aria-label="Delete social icon"
                        >
                            delete
                        </Styled.DeleteIcon>
                    )}
                </div>
            </Styled.ActiveIconHeader>
            <ValidatedLink
                type="text"
                customIcon={iconSrc}
                value={iconHref}
                id={`${title}-input`}
                placeholder={`${title
                    .split('-')
                    .map((word) => word[0].toUpperCase() + word.slice(1))
                    .join(' ')} link...`}
                width={165}
                onChange={(event) => setIconHref(event.target.value)}
            />
        </Styled.ActiveIconWrapper>
    );
};

ActiveIcon.propTypes = {
    title: PropTypes.string.isRequired,
    isAnimated: PropTypes.bool.isRequired,
    direction: PropTypes.string,
    activeIcons: PropTypes.arrayOf(PropTypes.object).isRequired,
    updateIconsOrder: PropTypes.func.isRequired,
    first: PropTypes.bool.isRequired,
    last: PropTypes.bool.isRequired,
    icon: PropTypes.object.isRequired,
    icons: PropTypes.arrayOf(PropTypes.object).isRequired,
    updateContent: PropTypes.func.isRequired,
    href: PropTypes.string.isRequired,
    iconSrc: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    hasFullStylingAccess: PropTypes.bool.isRequired,
};

ActiveIcon.defaultProps = {
    direction: '',
};

export default ActiveIcon;
